// moms-harness:anchor v2
// JIRA: LOGISPRD-14470
import type { ShipperOptionSource } from '@/components/system/shipperSelectFilter'
import { FREE_SHIPPERS, SHIPPERS } from '@/utils/consts'

/**
 * 계정이 고를 수 있는 화주 범위. 2.0 화면 공용 (LOGISPRD-14650).
 *
 * - `locked: false` — 통합 운영 상태(`FREE_SHIPPERS`). 기본 화주 없이 시작한다
 * - `locked: true` — 화주 계정. 화주명이 `shipperCode` 하나로 고정된다
 *
 * > **화면 일관성을 위한 고정이지 보안 경계가 아니다.** 세션은 sessionStorage 라 사용자가 고칠 수 있고
 * > API 를 직접 부르면 우회된다. 화주 범위의 강제는 BE 공통 화주 범위 가드 몫이다 (LOGISPRD-14505).
 */
export interface ShipperScope {
  locked: boolean
  shipperCode: string
  shipperName: string
  /** 잠긴 계정이 고를 수 있는 화주. 없으면 자기 화주 하나다 ([withCoMingle], LOGISPRD-15568) */
  selectable?: SelectableShipper[]
}

export interface SelectableShipper {
  shipperCode: string
  shipperName: string
}

/** 재고통합(`feature.coMingle.inboundEnabled`) 대상 화주. MUSINSA 는 잠기지 않으므로 넣지 않는다 (LOGISPRD-14782) */
const CO_MINGLE_SHIPPERS = ['29CM', 'INOOINT']

/** 재고통합 ON 에서 대상 계정이 자기 화주 말고 더 고를 수 있는 화주 (LOGISPRD-15568) */
const CO_MINGLE_EXTRA_SHIPPER: SelectableShipper = { shipperCode: SHIPPERS.MUSINSA, shipperName: SHIPPERS.MUSINSA }

/**
 * 재고통합 ON 에서 MUSINSA 를 더 고를 수 있는 잠긴 계정인가 (LOGISPRD-14782, LOGISPRD-15568).
 *
 * `FREE_SHIPPERS` 에 넣지 않는다 — 넣으면 잠금이 풀려 옵션이 전체 화주로 열린다([scopeShippers]).
 */
export const isCoMingleShipper = (scope: ShipperScope): boolean => scope.locked && CO_MINGLE_SHIPPERS.includes(scope.shipperCode)

/**
 * 세션 → 화주 범위. 통합 운영 판정은 1.0 `FilterBar` 렌더 규칙과 같다 — 현재 화주가 `FREE_SHIPPERS`
 * 4종이 아니면 고정한다. `is_convertible` 은 보지 않는다 (LOGISPRD-14650).
 *
 * ### 왜 `SPR_NM` 이 아니라 `shipper_code` 인가 (LOGISPRD-14470)
 *
 * 화주 전환(`MainHeader.onConvert`)은 `shipper_code`·`company`·`auth` 만 바꾸고 `SPR_NM` 은
 * 그대로 둔다. 초판은 `SPR_NM` 으로 기본 화주를 찾아, MASTER 가 GLOWNY 로 전환해 들어와도 `MUSINSA`
 * 로 찾았다 → MWP·MLT 목록에 없으니 첫 화주(`29CMOFFLINE`)로 떨어졌다. `shipper_code` 는 로그인 때
 * 서버 값으로, 전환 때 전환한 화주로 채워진다. 그 값이 없는 세션만 `SPR_NM` 을 쓴다.
 *
 * ### 화주를 알 수 없는 세션은 고정하지 않는다
 *
 * 둘 다 비면 고정할 화주가 없다. 빈 화주로 잠그면 옵션이 0개라 화주를 고를 수 없고, 화주 조건이
 * 빈 값이 되어 SKU ID 조회가 화주 제한 없이 나간다 — 고정한 척만 하는 상태다. 그래서 종전처럼
 * 고르게 둔다 (범위 강제는 BE 몫 — [ShipperScope] KDoc).
 */
export const shipperScopeOf = (user: { shipper_code?: string; shipper_name?: string; SPR_NM?: string }): ShipperScope => {
  const shipperCode = String(user.shipper_code || user.SPR_NM || '')
  return {
    locked: !!shipperCode && !FREE_SHIPPERS.includes(shipperCode),
    shipperCode,
    shipperName: String(user.shipper_name || shipperCode),
  }
}

/**
 * 재고통합 ON 이면 잠긴 29CM·INOOINT 계정이 자기 화주와 MUSINSA 를 고를 수 있게 한다 (LOGISPRD-15568).
 *
 * 자기 화주(`shipperCode`)는 바꾸지 않는다 — 기본 화주는 [defaultShipper] 로 판정한다. 자기 화주는 계속 고를 수 있다 (LOGISPRD-15718).
 * 그 밖의 계정과 `coMingle` false 는 받은 범위 그대로다. 화면은 `useCoMingleShipperScope` 로 진입 시점 FF 값을 받아 넘긴다.
 */
export const withCoMingle = (scope: ShipperScope, coMingle: boolean): ShipperScope =>
  coMingle && isCoMingleShipper(scope) ? { ...scope, selectable: [{ shipperCode: scope.shipperCode, shipperName: scope.shipperName }, CO_MINGLE_EXTRA_SHIPPER] } : scope

/** 잠긴 계정이 고를 수 있는 화주. 자기 화주가 첫 번째다 */
export const selectableShippers = (scope: ShipperScope): SelectableShipper[] =>
  scope.selectable ?? [{ shipperCode: scope.shipperCode, shipperName: scope.shipperName }]

/**
 * 화주 칸의 기본 화주 — 진입·초기화·복원 폴백에 쓴다 (LOGISPRD-15718).
 * 재고통합 대상 계정이 MUSINSA 를 고를 수 있으면([withCoMingle]) MUSINSA, 그 밖은 자기 화주다.
 */
export const defaultShipper = (scope: ShipperScope): SelectableShipper =>
  (isCoMingleShipper(scope) && selectableShippers(scope).find((s) => s.shipperCode === CO_MINGLE_EXTRA_SHIPPER.shipperCode)) || {
    shipperCode: scope.shipperCode,
    shipperName: scope.shipperName,
  }

/** 화주 칸을 고를 수 있는가. 통합 운영 계정, 또는 고를 화주가 둘 이상인 잠긴 계정 */
export const canSelectShipper = (scope: ShipperScope): boolean => !scope.locked || selectableShippers(scope).length > 1

/** 잠긴 계정의 화주 값을 고를 수 있는 화주로 맞춘다. 고를 수 있는 화주면 그대로, 아니면 기본 화주([defaultShipper]) */
export const scopedShipperCode = (scope: ShipperScope, shipperCode?: string): string =>
  shipperCode && selectableShippers(scope).some((s) => s.shipperCode === shipperCode) ? shipperCode : defaultShipper(scope).shipperCode

/**
 * 화주명 옵션 원본을 범위로 좁힌다. 화주 계정은 고를 수 있는 화주([selectableShippers])만 남긴다.
 *
 * 자기 화주가 MWP·MLT 목록에 없어도(M1P 화주 등) **세션 값으로 한 줄을 만든다.** 비워 두면
 * 화주명 칸이 비어 보이고, 남의 화주로 폴백하던 초판 버그와 같은 모양이 된다. (GR02 와 같은 처리 —
 * 화면마다 반복되는 이 로직의 공용화는 이 티켓 범위 밖이다)
 */
export const scopeShippers = (shippers: ShipperOptionSource[], scope: ShipperScope): ShipperOptionSource[] => {
  if (!scope.locked) return shippers
  return selectableShippers(scope).flatMap((selectable) => {
    const rows = shippers.filter((s) => s.shipperCode === selectable.shipperCode)
    return rows.length ? rows : [{ ...selectable, logisticsServiceType: '' }]
  })
}

/**
 * 조회 조건을 범위로 덮는다. 화주 계정은 고를 수 있는 화주가 아니면 — URL 복원 조건까지 — 기본 화주로
 * 조회한다([scopedShipperCode]). `shipperCode` 가 `serviceTypes` 를 이기므로(BE 규칙) 유형은 뺀다.
 */
export const lockParams = <T extends { shipperCode?: string; serviceTypes?: string[] }>(params: T, scope: ShipperScope): T =>
  scope.locked ? { ...params, shipperCode: scopedShipperCode(scope, params.shipperCode), serviceTypes: undefined } : params
